import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

// Small, session-scoped "compare tray" — lets a learner tick a handful of
// tutors/centres while browsing search results, then jump to /compare to
// see them side by side. Lives above the router (see App.js) so the
// selection survives navigating from search to the comparison page.
const CompareContext = createContext(null);

export const MAX_COMPARE = 3;
const STORAGE_KEY = "tutorslinks_compare_selection";

export const CompareProvider = ({ children }) => {
  const [selected, setSelected] = useState(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(selected));
    } catch {
      // sessionStorage unavailable (private browsing etc.) — selection just
      // won't survive a refresh, not worth surfacing to the user.
    }
  }, [selected]);

  const isSelected = useCallback(
    (id) => selected.some((s) => s.id === id),
    [selected],
  );

  // Returns true if the tutor was added, false if it was removed OR
  // rejected for being over the limit — callers use this to decide whether
  // to show a "limit reached" toast.
  const toggle = useCallback((entry) => {
    let added = false;
    setSelected((prev) => {
      const exists = prev.some((s) => s.id === entry.id);
      if (exists) {
        return prev.filter((s) => s.id !== entry.id);
      }
      if (prev.length >= MAX_COMPARE) {
        return prev;
      }
      added = true;
      return [...prev, entry];
    });
    return added;
  }, []);

  const remove = useCallback((id) => {
    setSelected((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const clear = useCallback(() => setSelected([]), []);

  return (
    <CompareContext.Provider
      value={{ selected, isSelected, toggle, remove, clear, maxCompare: MAX_COMPARE }}
    >
      {children}
    </CompareContext.Provider>
  );
};

export const useCompare = () => {
  const ctx = useContext(CompareContext);
  if (!ctx) throw new Error("useCompare must be used within a CompareProvider");
  return ctx;
};
